import { createBrowserRouter, Outlet, ScrollRestoration } from "react-router";
import { Navigation } from "./components/Navigation";
import { useLocation } from "react-router";
import { SmallFooter } from "./components/SmallFooter";
import { useEffect, lazy, Suspense } from "react";
import { RoutesEnum } from "./model/enum/RoutesEnum";

// Lazy load all page components
const Homepage = lazy(() =>
  import("./pages/homepage/Homepage").then((m) => ({ default: m.Homepage })),
);
const SolutionsPage = lazy(() =>
  import("./pages/solutions/SolutionsPage").then((m) => ({
    default: m.SolutionsPage,
  })),
);
const AboutCompany = lazy(() =>
  import("./pages/about/AboutCompany").then((m) => ({
    default: m.AboutCompany,
  })),
);
const ProjectsEU = lazy(() =>
  import("./pages/projects-eu/ProjectsEU").then((m) => ({
    default: m.ProjectsEU,
  })),
);
const ProjectsPage = lazy(() =>
  import("./pages/projects/ProjectsPage").then((m) => ({
    default: m.ProjectsPage,
  })),
);
const ContactPage = lazy(() =>
  import("./pages/contact/ContactPage").then((m) => ({
    default: m.ContactPage,
  })),
);
const ProjectDetail = lazy(() =>
  import("./pages/projects/components/ProjectDetail").then((m) => ({
    default: m.ProjectDetail,
  })),
);
const AdminLogin = lazy(() =>
  import("./pages/admin/AdminLogin").then((m) => ({ default: m.AdminLogin })),
);
const AdminDashboard = lazy(() =>
  import("./pages/admin/AdminDashboard").then((m) => ({
    default: m.AdminDashboard,
  })),
);
const AdminProjectsManagement = lazy(() =>
  import("./pages/admin/AdminProjectsManagement").then((m) => ({
    default: m.AdminProjectsManagement,
  })),
);
const NotFound = lazy(() =>
  import("./pages/NotFound").then((m) => ({ default: m.NotFound })),
);
const BlogPage = lazy(() =>
  import("./pages/blog/BlogPage").then((m) => ({ default: m.BlogPage })),
);
const BlogPostDetail = lazy(() =>
  import("./pages/blog/BlogPostDetail").then((m) => ({
    default: m.BlogPostDetail,
  })),
);
const AdminBlogManagement = lazy(() =>
  import("./pages/admin/AdminBlogManagement").then((m) => ({
    default: m.AdminBlogManagement,
  })),
);
const AdminBlogPostForm = lazy(() =>
  import("./pages/admin/AdminBlogPostForm").then((m) => ({
    default: m.AdminBlogPostForm,
  })),
);

// Loading component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
    </div>
  );
}

function Layout() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <Navigation />
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
      <SmallFooter />
      <ScrollRestoration />
    </>
  );
}

export const router = createBrowserRouter([
  {
    path: RoutesEnum.HOME,
    element: <Layout />,
    children: [
      {
        path: RoutesEnum.HOME,
        element: <Homepage />,
      },
      {
        path: `${RoutesEnum.SOLUTIONS}/:type`,
        element: <SolutionsPage />,
      },
      {
        path: RoutesEnum.ABOUT,
        element: <AboutCompany />,
      },
      {
        path: RoutesEnum.CONTACT,
        element: <ContactPage />,
      },
      {
        path: RoutesEnum.PROJECTS,
        element: <ProjectsPage />,
      },
      {
        path: `${RoutesEnum.PROJECTS}/:id`,
        element: <ProjectDetail />,
      },
      {
        path: RoutesEnum.EU_PROJECTS,
        element: <ProjectsEU />,
      },
      {
        path: RoutesEnum.GDPR,
        element: <SolutionsPage />,
      },
      {
        path: RoutesEnum.ADMIN_LOGIN,
        element: <AdminLogin />,
      },
      {
        path: RoutesEnum.ADMIN,
        element: <AdminDashboard />,
      },
      {
        path: RoutesEnum.ADMIN_PROJECTS,
        element: <AdminProjectsManagement />,
      },
      {
        path: RoutesEnum.BLOG,
        element: <BlogPage />,
      },
      {
        path: RoutesEnum.BLOG_POST,
        element: <BlogPostDetail />,
      },
      {
        path: RoutesEnum.ADMIN_BLOG,
        element: <AdminBlogManagement />,
      },
      {
        path: RoutesEnum.ADMIN_BLOG_NEW,
        element: <AdminBlogPostForm />,
      },
      {
        path: RoutesEnum.ADMIN_BLOG_EDIT,
        element: <AdminBlogPostForm />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);
